// src/components/ScorecardPage.js
import React, { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import {
    FaSmile,
    FaMeh,
    FaFrown,
    FaSadTear,
    FaArrowLeft,
    FaCheckCircle,
    FaTimesCircle,
    FaExclamationTriangle,
} from "react-icons/fa";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import Tools from "../utils/Tools.js";
import ScorecardEditModal from './ScorecardEditModal.js';

function ScorecardPage() {
    const [contact, setContact] = useState(null);
    const [rawScorecardResponse, setRawScorecardResponse] = useState(null);
    const [displayScorecardData, setDisplayScorecardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [showBackButton, setShowBackButton] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [fixedIntents, setFixedIntents] = useState([]);
    const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);

    useEffect(() => {
        // Check if there's a referrer in the location state
        const hasReferrer = Boolean(location.state?.referrer);

        // Check if we have browser history to go back to
        const hasHistory = window.history.length > 1;

        // Only show the back button if we have somewhere to go back to
        setShowBackButton(hasReferrer || hasHistory);
    }, [location]);

    const fetchFixedIntents = useCallback((contactId) => {
        const queryParams = new URLSearchParams();
        queryParams.append("contact_id", contactId);
        return Tools.sendRequest("get-fixed-taxonomy-by-contactid", queryParams.toString()).then((data) => {
            setFixedIntents(data.chartData || []);
        });
    }, []);

    const fetchContactData = useCallback(() => {
        const queryParams = new URLSearchParams();
        queryParams.append("contactId", id);
        return Tools.sendRequest("get-contact", queryParams.toString()).then((data) => {
            setContact(data);
            // Fetch fixed intents after we have the contact data
            if (data.contact_id) {
                fetchFixedIntents(data.contact_id);
            }
        });
    }, [id, fetchFixedIntents]);

    const fetchScorecardData = useCallback(() => {
        const queryParams = new URLSearchParams();
        queryParams.append("contactId", id);
        return Tools.sendRequest("get-scorecard-data", queryParams.toString()).then((response) => {
            if (response.success && response.data.scorecard) {
                // Store raw response
                setRawScorecardResponse(response.data);
                
                // Transform for display (existing logic)
                const { scorecard, config } = response.data;
                
                // Create sections with merged config and results
                const sectionsWithConfig = scorecard.sections.map(sectionResult => {
                    const sectionConfig = config.sections.find(s => s.section_id === sectionResult.section_id);
                    
                    const sectionCriteria = scorecard.criteria
                        .filter(c => {
                            const criteriaConfig = config.criteria.find(cc => cc.criteria_id === c.criteria_id);
                            return criteriaConfig?.section_id === sectionResult.section_id;
                        })
                        .map(criteriaResult => {
                            const criteriaConfig = config.criteria.find(cc => cc.criteria_id === criteriaResult.criteria_id);
                            
                            return {
                                ...criteriaResult,
                                criteria_name: criteriaConfig?.label || 'Unknown Criteria',
                                max_points: criteriaConfig?.max_points || 100,
                                failure_reasons: criteriaResult.failure_reason_ids?.map(frId => {
                                    const failureReason = config.failureReasons.find(fr => fr.failure_reason_id === frId);
                                    return failureReason?.label || 'Unknown Failure Reason';
                                }) || []
                            };
                        });

                    // Calculate section max points by summing max_points of criteria with non-null scores
                    const sectionMaxPoints = sectionCriteria
                        .filter(criteria => criteria.score !== null)
                        .reduce((sum, criteria) => sum + criteria.max_points, 0);
                    
                    return {
                        ...sectionResult,
                        section_name: sectionConfig?.label || 'Unknown Section',
                        max_points: sectionMaxPoints,
                        criteria: sectionCriteria
                    };
                });

                // Create autofails with merged config
                const autofailsWithConfig = scorecard.autofails.map(autofailResult => {
                    const autofailConfig = config.autofails.find(a => a.autofail_id === autofailResult.autofail_id);
                    return {
                        ...autofailResult,
                        autofail_name: autofailConfig?.label || 'Unknown Autofail'
                    };
                });

                setDisplayScorecardData({
                    ...scorecard,
                    sections: sectionsWithConfig,
                    autofails: autofailsWithConfig.filter(a => a.is_failed), // Only show failed autofails as requested
                    autofailed: autofailsWithConfig.some(a => a.is_failed), // Set overall autofail status
                    config: config
                });
            } else {
                setRawScorecardResponse(null);
                setDisplayScorecardData(null);
            }
        });
    }, [id]);
    
    useEffect(() => {
        Promise.all([fetchContactData(), fetchScorecardData()])
            .finally(() => setLoading(false));
    }, [fetchContactData, fetchScorecardData]);

    const handleBackClick = () => {
        navigate(location.state?.referrer || -1);
    };

    const formatTime = (seconds) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const remainingSeconds = Math.floor(seconds % 60);

        let result = "";

        if (hours > 0) {
            result += hours + (hours === 1 ? " hour " : " hours ");
        }

        if (minutes > 0 || (hours > 0 && (remainingSeconds > 0 || minutes === 0))) {
            result += minutes + (minutes === 1 ? " minute " : " minutes ");
        }

        if (
            remainingSeconds > 0 ||
            (hours === 0 && minutes === 0 && remainingSeconds === 0)
        ) {
            result +=
                remainingSeconds + (remainingSeconds === 1 ? " second" : " seconds");
        }

        return result.trim();
    };

    const formatTimestamp = (date) => {
        return new Date(date).toLocaleString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
            hour: "numeric",
            minute: "numeric",
            timeZoneName: "short",
        });
    };

    const getScoreColor = (score) => {
        if (score >= 80) return "text-green-600";
        if (score >= 60) return "text-yellow-600";
        return "text-red-600";
    };

    const getScoreIcon = (isFailed) => {
        return isFailed ? 
            <FaTimesCircle className="text-red-500 inline ml-1" /> : 
            <FaCheckCircle className="text-green-500 inline ml-1" />;
    };

    if (loading || !contact) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="loader"></div>
            </div>
        );
    }

    const handleDownloadPDF = async () => {
        if (!contact || !displayScorecardData) return;
        setIsGeneratingPDF(true);
        
        try {
            const response = await Tools.sendRequest("generate-scorecard-pdf", {
                contact_id: contact.contact_id  // Just the ID!
            }, "POST");

            console.log('Server response:', response);
            
            // Same blob handling code (unchanged)
            const byteCharacters = atob(response.pdfBuffer);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: 'application/pdf' });
            
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = response.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error generating PDF:', error);
        } finally {
            setIsGeneratingPDF(false);
        }
    };

    const handleSaveScorecard = async (saveData) => {
        const { contactId, changes } = saveData;

        try {
            const response = await Tools.sendRequest("update-scorecard", {
                contactId: contactId,
                changes: changes,
                // Add aggregation keys from contact data
                aggregationKeys: {
                    date: new Date(contact.call_date).toISOString().split('T')[0], // Fixed field name
                    agent: contact.agent || '',
                    queue: contact.queue || '',
                    call_origination: contact.initiation_method || '',
                    is_weekend: contact.is_weekend || 0
                },
                // Add categories
                categories: contact.categoriesWithIds || []
            }, "POST");
            
            if (response.success) {
                await fetchScorecardData();
            } else {
                alert("Error saving scorecard: " + response.message);
            }
        } catch (error) {
            console.error("Error saving scorecard:", error);
            alert("Error saving scorecard");
        }
    };

    return (
        <div className="container mx-auto p-6">
            <div className="flex items-center mb-6">
                {showBackButton && (
                    <button
                        className="text-gray-600 hover:text-gray-800 flex items-center"
                        onClick={handleBackClick}
                    >
                        <FaArrowLeft className="mr-1" /> Back
                    </button>
                )}
                
                <div className="flex items-center gap-3 ml-auto">
                    <button
                        className={`bg-teal-600 hover:bg-teal-700 text-white font-medium py-2 px-4 rounded flex items-center transition ${
                            isGeneratingPDF || !contact || !displayScorecardData ? "opacity-70 cursor-not-allowed" : ""
                        }`}
                        onClick={handleDownloadPDF}
                        disabled={isGeneratingPDF || !contact || !displayScorecardData}
                    >
                        {isGeneratingPDF ? (
                            <>
                                <span className="mr-2 animate-spin">⟳</span>
                                Generating PDF...
                            </>
                        ) : (
                            "Download PDF"
                        )}
                    </button>
                    <button
                        className="bg-cyan-600 hover:bg-cyan-700 text-white font-medium py-2 px-4 rounded"
                        onClick={() => setIsEditModalOpen(true)}
                        disabled={!contact || !displayScorecardData}
                    >
                        Edit Scorecard
                    </button>
                    <button
                        className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-4 rounded"
                        onClick={() => {
                            navigate(`/contact/${contact.contact_id}`);
                        }}
                        onContextMenu={(event) => {
                            event.preventDefault();

                            // Remove any existing menu to prevent multiple menus
                            const existingMenu = document.getElementById("custom-menu");
                            if (existingMenu) {
                                document.body.removeChild(existingMenu);
                            }

                            // Create the menu
                            const menu = document.createElement("div");
                            menu.id = "custom-menu";
                            menu.style.position = "absolute";
                            menu.style.left = `${event.pageX}px`;
                            menu.style.top = `${event.pageY}px`;
                            menu.style.border = "1px solid #ccc";
                            menu.style.padding = "8px";
                            menu.style.zIndex = 1000;
                            menu.style.backgroundColor = "white";
                            menu.style.boxShadow = "0 2px 10px rgba(0,0,0,0.1)";
                            menu.style.borderRadius = "4px";

                            // Create the "Open in New Window" option
                            const openInNewWindowOption = document.createElement("div");
                            openInNewWindowOption.textContent = "Open in New Window";
                            openInNewWindowOption.style.cursor = "pointer";
                            openInNewWindowOption.style.padding = "4px 8px";
                            openInNewWindowOption.onmouseover = () => {
                                openInNewWindowOption.style.backgroundColor = "#f3f4f6";
                            };
                            openInNewWindowOption.onmouseout = () => {
                                openInNewWindowOption.style.backgroundColor = "transparent";
                            };
                            openInNewWindowOption.onclick = () => {
                                window.open(`/contact/${contact.contact_id}`, "_blank");
                                if (menu.parentNode) {
                                    menu.parentNode.removeChild(menu);
                                }
                            };

                            menu.appendChild(openInNewWindowOption);
                            document.body.appendChild(menu);

                            // Remove the menu when clicking outside
                            const handleClickOutside = (event) => {
                                if (menu && !menu.contains(event.target)) {
                                    if (menu.parentNode) {
                                        menu.parentNode.removeChild(menu);
                                    }
                                    document.removeEventListener("click", handleClickOutside);
                                }
                            };

                            document.addEventListener("click", handleClickOutside);
                        }}
                    >
                        View Contact Details
                    </button>
                </div>
            </div>

            {/* Header */}
            {/* <div className="flex items-center justify-between mb-8">
                <h1 className="text-4xl font-bold">Evaluation Details</h1>
            </div> */}

            <div className="grid grid-cols-1 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {/* Metadata Panel */}
                <div className="lg:col-span-1 xl:cold-span-1">
                    <div className="bg-white shadow rounded-lg p-6 bg-gradient-to-br from-blue-50 to-blue-100 sticky top-6">
                        <h2 className="text-xl font-semibold mb-4">Evaluation Details</h2>
                        
                        {/* Contact Information */}
                        <div className="mb-6">
                            <h3 className="text-lg font-medium mb-3 text-gray-700">Contact Details</h3>
                            <div className="space-y-2 text-sm">
                                <p><strong>Date:</strong> {formatTimestamp(contact.call_date)}</p>
                                <p><strong>Duration:</strong> {formatTime(contact.duration)}</p>
                                <p><strong>Contact ID:</strong> {contact.contact_id}</p>
                                <p><strong>ANI:</strong> {contact.ani}</p>
                                <p><strong>Origination:</strong> {contact.initiation_method}</p>
                                <p><strong>Queue:</strong> {contact.queue}</p>
                                <p><strong>Agent:</strong> {contact.agent}</p>
                                <p><strong>Categories:</strong>
                                    <span className="ml-2">
                                        {contact.categories &&
                                            contact.categories.map((category, index) => (
                                                <span
                                                    key={index}
                                                    className="bg-green-100 text-green-800 text-xs font-medium mr-2 mb-2 px-2.5 py-0.5 rounded inline-block"
                                                >
                                                    {category ? category.trim() : "None"}
                                                </span>
                                            ))}
                                    </span>
                                </p>
                                <p><strong>Intents:</strong>
                                    <span className="ml-2">
                                        {contact.intents_agg_json &&
                                            contact.intents_agg_json.map((intent, index) => (
                                                <Tippy
                                                    key={index}
                                                    content={
                                                        <div>
                                                            <div>
                                                                <span className="text-xs text-gray-400">
                                                                    Category:{" "}
                                                                </span>
                                                                {intent.category}
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-400">
                                                                    Major Intent:{" "}
                                                                </span>
                                                                {intent.major_intent}
                                                            </div>
                                                            <div>
                                                                <span className="text-xs text-gray-400">
                                                                    Minor Intent:{" "}
                                                                </span>
                                                                {intent.minor_intent}
                                                            </div>
                                                        </div>
                                                    }
                                                >
                                                    <span className="bg-blue-100 text-blue-800 text-xs font-medium mr-2 mb-2 px-2.5 py-0.5 rounded inline-block cursor-pointer">
                                                        {intent.major_intent}
                                                    </span>
                                                </Tippy>
                                            ))}
                                    </span>
                                </p>
                                <p><strong>Fixed Intents:</strong>
                                    <span className="ml-2">
                                        {fixedIntents && fixedIntents.length > 0 ? 
                                            fixedIntents.map((intent, index) => (
                                                <Tippy
                                                    key={index}
                                                    content={
                                                        <div>
                                                            <div>
                                                                <span className="text-xs text-gray-400">
                                                                    Category: {" "}
                                                                </span>
                                                                {intent.category_name}
                                                            </div>
                                                        </div>
                                                    }
                                                >
                                                    <span className="bg-purple-100 text-purple-800 text-xs font-medium mr-2 mb-2 px-2.5 py-0.5 rounded inline-block cursor-pointer">
                                                        {intent.intent_name}
                                                    </span>
                                                </Tippy>
                                            )) :
                                            <span className="text-gray-500 text-xs">None</span>
                                        }
                                    </span>
                                </p>
                            </div>
                        </div>


                        {/* Scorecard Information */}
                        {displayScorecardData && (
                            <div>
                                <h3 className="text-lg font-medium mb-3 text-gray-700">Scorecard Details</h3>
                                <div className="space-y-2 text-sm">
                                    <p className="flex items-center">
                                        <strong>Total Score:</strong> 
                                        <span className={`ml-2 font-bold ${getScoreColor(displayScorecardData.final_score)}`}>
                                            {displayScorecardData.final_score}%
                                        </span>
                                    </p>
                                    <p><strong>Generated:</strong> {formatTimestamp(displayScorecardData.created_at)}</p>
                                    {/* Add Outage Type */}
                                    {/*<p><strong>Outage Type:</strong> {
                                        displayScorecardData.outage_type
                                            ? displayScorecardData.outage_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                                            : "Not applicable"
                                    }<p/>
                                    */}

                                    {/* Add Outage Verbatim Score */}
                                    {/* {displayScorecardData.outage_verbatim_score !== null && displayScorecardData.outage_verbatim_score !== undefined && (
                                        <p className="flex items-center">
                                            <strong>Verbatim Script Compliance:</strong> 
                                            <span className={`ml-2 font-bold ${getScoreColor(displayScorecardData.outage_verbatim_score)}`}>
                                                {displayScorecardData.outage_verbatim_score}%
                                            </span>
                                        </p>
                                    )} */}
                                    <p className="flex items-center">
                                        <strong>Autofailed:</strong> 
                                        {displayScorecardData.autofailed ? (
                                            <span className="ml-2 text-red-600 flex items-center">
                                                <FaExclamationTriangle className="mr-1" /> Yes
                                            </span>
                                        ) : (
                                            <span className="ml-2 flex items-center">
                                                No
                                            </span>
                                        )}
                                    </p>
                                    <p><strong>Agent Went Above & Beyond:</strong> {displayScorecardData.agent_went_above_and_beyond ? "Yes" : "No"}</p>
                                    <p><strong>Caller Indicated Initial Frustration:</strong> {displayScorecardData.caller_frustration_beginning ? "Yes" : "No"}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Scorecard Panel */}
                <div className="lg:col-span-2 xl:col-span-3">
                    <div className="bg-white shadow rounded-lg p-6">
                        <div className="border-b pb-4 mb-6 flex items-center justify-between">
                            <h1 className="text-2xl font-bold text-gray-800">Quality Scorecard</h1>
                            <div className={`text-2xl font-bold ${getScoreColor(displayScorecardData?.final_score || 0)}`}>
                                Overall Score: {displayScorecardData?.final_score || 0}%
                            </div>
                        </div>

                        {displayScorecardData && (
                            <div className="space-y-6">
                                {/* Sections */}
                                {displayScorecardData.sections.map((section) => (
                                    <div key={section.section_id} className="border rounded-lg p-4 bg-gray-50">
                                        {/* Section header */}
                                        <div className="flex items-center justify-between mb-4">
                                            <h3 className="text-lg font-semibold text-gray-800">
                                                {section.section_name}
                                            </h3>
                                            <div className="text-right">
                                                <div className={`text-lg font-bold ${section.max_points === 0 ? '' : getScoreColor(Math.round((section.section_score / section.max_points) * 100))}`}>
                                                    {section.max_points === 0 
                                                        ? "N/A" 
                                                        : `${section.section_score}/${section.max_points} = ${Math.round((section.section_score / section.max_points) * 100)}%`
                                                    }
                                                </div>
                                            </div>
                                        </div>

                                        {/* Criteria */}
                                        <div className="space-y-3">
                                            {section.criteria.map((criteria) => (
                                                <div key={criteria.criteria_id} className="bg-white p-3 rounded border">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-700 mb-1">
                                                                {criteria.criteria_name}
                                                                {/* Show icon when score is not null */}
                                                                {criteria.score !== null && getScoreIcon(criteria.is_failed)}
                                                            </p>
                                                            {criteria.genai_comment && (
                                                                <p className="text-sm text-gray-600 mb-2">
                                                                    <strong>Comment:</strong> {criteria.genai_comment}
                                                                </p>
                                                            )}
                                                            {criteria.failure_reasons && criteria.failure_reasons.length > 0 && (
                                                                <div className="text-sm text-red-600">
                                                                    <strong>Failure Reasons:</strong>
                                                                    <ul className="list-disc list-inside mt-1">
                                                                        {criteria.failure_reasons.map((reason, index) => (
                                                                            <li key={index}>{reason}</li>
                                                                        ))}
                                                                    </ul>
                                                                </div>
                                                            )}
                                                            {/* Show "Not Applicable" when score is null */}
                                                            {criteria.score === null && (
                                                                <p className="text-sm text-gray-500 italic">
                                                                    Not Applicable
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right ml-4">
                                                            {/* Show score based on is_failed and max_points, or N/A if score is null */}
                                                            {criteria.score !== null ? (
                                                                <div className={`font-bold ${criteria.is_failed ? 'text-red-600' : 'text-green-600'}`}>
                                                                    {criteria.is_failed ? 0 : criteria.max_points}/{criteria.max_points}
                                                                </div>
                                                            ) : (
                                                                <div className="text-gray-400 text-sm">
                                                                    N/A
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}

                                {/* Autofails */}
                                {displayScorecardData.autofails && displayScorecardData.autofails.length > 0 && (
                                    <div className="border rounded-lg p-4 bg-red-50 border-red-200">
                                        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                                            <FaExclamationTriangle className="text-red-500 mr-2" />
                                            Autofail Elements
                                        </h3>
                                        <div className="space-y-3">
                                            {displayScorecardData.autofails.map((autofail) => (
                                                <div key={autofail.autofail_id} className="bg-white p-3 rounded border border-red-200">
                                                    <div className="flex items-start justify-between">
                                                        <div className="flex-1">
                                                            <p className="font-medium text-gray-700 mb-1">
                                                                {autofail.autofail_name}
                                                                {autofail.is_failed ? 
                                                                    <FaTimesCircle className="text-red-500 inline ml-1" /> : 
                                                                    <FaCheckCircle className="text-green-500 inline ml-1" />
                                                                }
                                                            </p>
                                                            {autofail.genai_comment && (
                                                                <p className="text-sm text-gray-600">
                                                                    <strong>Comment:</strong> {autofail.genai_comment}
                                                                </p>
                                                            )}
                                                        </div>
                                                        <div className="text-right ml-4">
                                                            <div className={`font-bold ${autofail.is_failed ? 'text-red-600' : 'text-green-600'}`}>
                                                                {autofail.is_failed ? 'FAILED' : 'PASSED'}
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {!displayScorecardData && (
                            <div className="text-center py-8 text-gray-500">
                                <p>No scorecard data available for this contact.</p>
                                <p className="text-sm mt-2">Generate a scorecard to see detailed evaluation results.</p>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {/* Edit Modal */}
            <ScorecardEditModal
                isOpen={isEditModalOpen}
                onClose={() => setIsEditModalOpen(false)}
                contact={contact}
                scorecardData={rawScorecardResponse?.scorecard || null}
                config={rawScorecardResponse?.config || null}
                onSave={handleSaveScorecard}
            />
        </div>
    );
}

export default ScorecardPage;